import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, CircularProgress, FormHelperText, InputLabel, MenuItem, Stack, TextField } from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import React, { useEffect, useState } from "react";

import { Option } from "components/common/Autocomplete/Autocomplete";
import { HeaderInput } from "components/detector/HeaderInput";
import { DetectorGroupAutocomplete } from "components/detectorManagement/FilterDetector/DetectorGroupAutocomplete/DetectorGroupAutocomplete";
import { DEFAULT_DETECTOR_GROUP, GROUNDLIGHT_SUPPORT_EMAIL, STABLE_DETECTOR_MODES } from "config/constants";
import { NewDetectorForm } from "hooks/CreateDetector/useCreateDetectorForm";
import { useAppSelector } from "hooks/store";
import { Typography } from "new-components/typography/Typography";
import { getUserState } from "store/slices/Auth/authSlice";
import { DetectorModes } from "types/createDetector";

import { DetectorCreationSettings } from "./Step2";

type DetectorModeOption = {
  value: DetectorModes;
  label: string;
  description: string;
  hasAccess: boolean;
};

export interface DetectorGroup {
  id: string;
  name: string;
}

export interface Step1Props {
  newDetectorForm: NewDetectorForm;
  onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSelectDetectorFormChange: (event: SelectChangeEvent) => void;
  onGroupChange: (option?: Option<string>) => void;
  showCreateGroup: boolean;
  nameError: string;
  detectorCreationSettings: DetectorCreationSettings;
  isLoadingCreationSettings?: boolean;
}

export function Step1(props: Step1Props) {
  const {
    newDetectorForm,
    onInputChange,
    onSelectDetectorFormChange,
    showCreateGroup,
    nameError,
    onGroupChange: onGroupFormChange,
    isLoadingCreationSettings,
    detectorCreationSettings,
  } = props;

  const userInfo = useAppSelector(getUserState);

  const { answerMode, detectorName, groupOption, groupName } = newDetectorForm;

  const [exploreError, setExploreError] = useState<string>("");

  const [hasDetectorNameChanged, setHasDetectorNameChanged] = useState<boolean>(false);

  const detectorModeOptions: DetectorModeOption[] = [
    { value: DetectorModes.BINARY, label: "Binary", description: "Yes/No", hasAccess: true },
    {
      value: DetectorModes.COUNT,
      label: "Count",
      description: "Returns whole number in response to query",
      hasAccess: userInfo.enable_count_mode,
    },
    {
      value: DetectorModes.MULTICHOICE,
      label: "Multiple Choice - Choose one",
      description: "Returns single answer from list",
      hasAccess: userInfo.enable_multi_class_mode,
    },
    {
      value: DetectorModes.TEXT_RECOGNITION,
      label: "Text Recognition",
      description: "Extracts text from images",
      hasAccess: userInfo.enable_text_mode,
    },
    {
      value: DetectorModes.BOUNDING_BOX,
      label: "Bounding Boxes",
      description: "Returns bounding boxes for detected objects",
      hasAccess: true,
    },
  ];

  useEffect(() => {
    if (groupName === DEFAULT_DETECTOR_GROUP) {
      setExploreError("Explore is restricted. Try another");
    } else {
      setExploreError("");
    }
  }, [groupName, showCreateGroup]);

  return (
    <Box className="flex flex-col space-y-6 my-8">
      <Box className="flex justify-between gap-4">
        <Box className="flex flex-col w-[50%]">
          <InputLabel className="text-sm md:text-base mb-2 text-black">Name:</InputLabel>
          <TextField
            type="text"
            data-testid="detectorName"
            name="detectorName"
            placeholder="Enter Detector Name"
            value={detectorName}
            onChange={(e) => {
              setHasDetectorNameChanged(true);
              onInputChange(e);
            }}
            FormHelperTextProps={{
              sx: { textTransform: "initial" },
            }}
            size="small"
            className="bg-zinc-50"
            error={!!nameError}
            sx={{ ".MuiInputBase-root": { color: hasDetectorNameChanged ? "common.black" : "text.disabled" } }}
          />

          {nameError && (
            <FormHelperText error className="truncate m-2 mb-0">
              {nameError}
            </FormHelperText>
          )}
        </Box>

        <Box className="flex flex-col w-[50%]">
          <InputLabel className="text-sm md:text-base mb-2 text-black">Group:</InputLabel>
          <DetectorGroupAutocomplete
            hasCreateOption
            hasDefaultOption
            autoSelectFirst
            showLoadingIndicator
            label=""
            onChange={(option) => onGroupFormChange(option)}
            value={groupOption}
            popupIcon={<KeyboardArrowDownIcon sx={{ color: "black" }} />}
            classes={{
              root: "bg-neutral-lightest",
            }}
            sx={{
              background: "white",
            }}
          />

          {showCreateGroup ? (
            <>
              {exploreError && <Typography className="truncate text-red-700 m-2 mb-0">{exploreError}</Typography>}
              <HeaderInput
                label="New group name:"
                value={groupName}
                disabled={false}
                name="groupName"
                placeholder="Enter New Group Name"
                onChange={onInputChange}
                borderClassName="bg-white border-none grow mt-3"
                valueClassName="overflow-x-auto placeholder-shown:text-ellipsis"
                tagClassName="ml-2 min-w-[140px] font-semibold text-sm md:text-base"
              />
            </>
          ) : undefined}
        </Box>
      </Box>

      <Box className="flex flex-col min-w-full">
        <InputLabel className="text-sm md:text-base text-black mb-2">Answer Mode:</InputLabel>
        <Select
          data-testid="answerMode"
          name="answerMode"
          value={answerMode}
          onChange={onSelectDetectorFormChange}
          IconComponent={KeyboardArrowDownIcon}
          sx={{
            ".MuiSelect-icon": {
              color: "black",
            },
          }}
          className="w-full h-[42px] bg-zinc-50 text-xl"
        >
          {detectorModeOptions.map((detectorModeOption) => (
            <MenuItem
              value={detectorModeOption.value}
              key={detectorModeOption.value}
              disabled={
                (detectorModeOption.value === DetectorModes.COUNT && !userInfo.enable_count_mode) ||
                (detectorModeOption.value === DetectorModes.TEXT_RECOGNITION && !userInfo.enable_text_mode) ||
                (detectorModeOption.value === DetectorModes.MULTICHOICE && !userInfo.enable_multi_class_mode) ||
                (detectorModeOption.value === DetectorModes.BOUNDING_BOX && !userInfo.enable_bounding_box_mode)
              }
            >
              <Box display="flex">
                <Typography fontWeight="bold" mr={1}>
                  {detectorModeOption.label}
                </Typography>

                {!STABLE_DETECTOR_MODES.includes(detectorModeOption.value) && (
                  <Typography variant="small" fontWeight="bold" className="mr-3">
                    (BETA)
                  </Typography>
                )}

                <Typography>
                  ({detectorModeOption.description}
                  {!detectorModeOption.hasAccess && " - upgrade your plan to unlock this feature"})
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </Box>

      {answerMode === DetectorModes.COUNT && (
        <Typography data-testid="countModeNote">
          {"NOTE: Count mode is best suited for tasks that a typical person could accomplish in < 30 seconds (usually < 20 objects)."}
          <br />
          For a custom-configured detector to suit your higher count application, contact {" "}
          <a href={`mailto:${GROUNDLIGHT_SUPPORT_EMAIL}`} className="underline text-primary-light">
            {GROUNDLIGHT_SUPPORT_EMAIL}
          </a>
          .
        </Typography>
      )}

      {answerMode === DetectorModes.TEXT_RECOGNITION && (
        <Stack>
          <Typography data-testid="textRecognitionNote">
            NOTE: Text recognition mode is best suited for use with English language and numerals. 
            <br />
            For a custom-configured detector to suit your specific application, contact{" "}
            <a href={`mailto:${GROUNDLIGHT_SUPPORT_EMAIL}`} className="underline text-primary-light">
              {GROUNDLIGHT_SUPPORT_EMAIL}
            </a>
            .
          </Typography>
          <Typography>
            Text recognition character limit:{" "}
            {isLoadingCreationSettings ? (
              <CircularProgress color="inherit" sx={{ width: "12px!important", height: "12px!important" }} />
            ) : (
              detectorCreationSettings.MAX_LENGTH
            )}
          </Typography>
        </Stack>
      )}
      {answerMode === DetectorModes.MULTICHOICE && (
        <Typography data-testid="multiChoiceNote">
          NOTE: Current version optimized for 6 or fewer options. If using more than 6, cloud labeler response times
          may be slower.
          <br />
          For a custom-configured detector to suit your specific application, contact {" "}
          <a href={`mailto:${GROUNDLIGHT_SUPPORT_EMAIL}`} className="underline text-primary-light">
            {GROUNDLIGHT_SUPPORT_EMAIL}
          </a>
          .
        </Typography>
      )}

      {answerMode === DetectorModes.BOUNDING_BOX && (
        <Typography data-testid="boundingBoxNote">
          {"NOTE: Bounding boxes mode is best suited for tasks that a typical person could accomplish in under 30 seconds (usually < 20 objects)."}
          <br />
          For a custom-configured detector to suit your specific application, contact {" "}
          <a href={`mailto:${GROUNDLIGHT_SUPPORT_EMAIL}`} className="underline text-primary-light">
            {GROUNDLIGHT_SUPPORT_EMAIL}
          </a>
          .
        </Typography>
      )}
    </Box>
  );
}
